// Admin Dashboard - Simple management tool
import { useState, useEffect } from 'react';
import { Card, Button, useToast } from '@components/ui';
import { Users, FileText, Activity, TrendingUp, Clock } from 'lucide-react';
import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/useAuthStore';

interface DashboardContent {
  id: string;
  title: string;
  description: string;
  type: 'change' | 'news';
  visible: boolean;
  created_at: string;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  totalFiles: number;
  totalStorage: number;
  recentSignups: number;
}

export const AdminDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'overview' | 'content' | 'users'>('overview');
  const [dashboardContent, setDashboardContent] = useState<DashboardContent[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingContent, setEditingContent] = useState<DashboardContent | null>(null);
  const [newContentTitle, setNewContentTitle] = useState('');
  const [newContentDesc, setNewContentDesc] = useState('');
  const [newContentType, setNewContentType] = useState<'change' | 'news'>('change');

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load dashboard content
      const { data: content, error: contentError } = await supabase
        .from('dashboard_content')
        .select('*')
        .order('created_at', { ascending: false });

      if (contentError) {
        // Table might not exist yet, that's okay
        console.log('Dashboard content table not found:', contentError);
        setDashboardContent([]);
      } else {
        setDashboardContent(content || []);
      }

      // Load user stats
      const { data: users } = await supabase.from('profiles').select('id, created_at');
      const { data: files } = await supabase.from('files').select('id, size');
      
      const totalUsers = users?.length || 0;
      const recentSignups = users?.filter(u => {
        const signupDate = new Date(u.created_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return signupDate > weekAgo;
      }).length || 0;

      const totalFiles = files?.length || 0;
      const totalStorage = files?.reduce((sum, f) => sum + (f.size || 0), 0) || 0;

      // Active users (users with activity in last 24h) - simplified
      const { data: activeSessions } = await supabase.auth.admin.listUsers();
      const activeUsers = activeSessions?.users?.length || 0;

      setUserStats({
        totalUsers,
        activeUsers,
        totalFiles,
        totalStorage,
        recentSignups,
      });
    } catch (error) {
      console.error('Failed to load admin data:', error);
      showToast('error', 'Error', 'Failed to load admin data.');
    } finally {
      setIsLoading(false);
    }
  };

  const saveContent = async () => {
    if (!newContentTitle.trim() || !newContentDesc.trim()) {
      showToast('error', 'Validation', 'Title and description are required.');
      return;
    }

    try {
      if (editingContent) {
        // Update existing
        const { error } = await supabase
          .from('dashboard_content')
          .update({
            title: newContentTitle,
            description: newContentDesc,
            type: newContentType,
          })
          .eq('id', editingContent.id);

        if (error) throw error;
        
        // Update only the specific item in state instead of reloading everything
        setDashboardContent(prevContent =>
          prevContent.map(item =>
            item.id === editingContent.id
              ? { ...item, title: newContentTitle, description: newContentDesc, type: newContentType }
              : item
          )
        );
        
        showToast('success', 'Updated', 'Content updated successfully.');
      } else {
        // Create new
        const { data, error } = await supabase
          .from('dashboard_content')
          .insert({
            title: newContentTitle,
            description: newContentDesc,
            type: newContentType,
            visible: true,
          })
          .select()
          .single();

        if (error) throw error;
        
        // Add new item to state instead of reloading everything
        if (data) {
          setDashboardContent(prevContent => [data, ...prevContent]);
        }
        
        showToast('success', 'Created', 'New content created.');
      }

      setNewContentTitle('');
      setNewContentDesc('');
      setEditingContent(null);
      // Removed loadData() - now only updates the affected item
    } catch (error: any) {
      console.error('Failed to save content:', error);
      showToast('error', 'Error', error.message || 'Failed to save content.');
    }
  };

  const toggleVisibility = async (id: string, currentVisible: boolean) => {
    try {
      const { error } = await supabase
        .from('dashboard_content')
        .update({ visible: !currentVisible })
        .eq('id', id);

      if (error) throw error;
      
      // Update only the specific item in state instead of reloading everything
      setDashboardContent(prevContent =>
        prevContent.map(item =>
          item.id === id
            ? { ...item, visible: !currentVisible }
            : item
        )
      );
      
      showToast('success', 'Updated', 'Visibility toggled.');
      // Removed loadData() - now only updates the affected item
    } catch (error: any) {
      showToast('error', 'Error', error.message);
    }
  };

  const deleteContent = async (id: string) => {
    if (!confirm('Delete this content item?')) return;

    try {
      const { error } = await supabase
        .from('dashboard_content')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      // Remove only the specific item from state instead of reloading everything
      setDashboardContent(prevContent =>
        prevContent.filter(item => item.id !== id)
      );
      
      showToast('success', 'Deleted', 'Content deleted.');
      // Removed loadData() - now only removes the affected item
    } catch (error: any) {
      showToast('error', 'Error', error.message);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-100 p-4 md:p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60 font-inter">Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-dela text-2xl md:text-3xl text-white mb-1">
              Admin Dashboard
            </h1>
            <p className="text-white/50 font-inter text-sm">
              Manage dashboard content and monitor users
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-white/10">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-inter font-medium text-sm transition-colors ${
              activeTab === 'overview'
                ? 'text-primary border-b-2 border-primary'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`px-4 py-2 font-inter font-medium text-sm transition-colors ${
              activeTab === 'content'
                ? 'text-primary border-b-2 border-primary'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Dashboard Content
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-inter font-medium text-sm transition-colors ${
              activeTab === 'users'
                ? 'text-primary border-b-2 border-primary'
                : 'text-white/60 hover:text-white'
            }`}
          >
            User Monitoring
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && userStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4 border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white/50 font-inter text-xs">Total Users</p>
                  <p className="font-inter font-bold text-white text-xl">{userStats.totalUsers}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Activity className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-white/50 font-inter text-xs">Active Users</p>
                  <p className="font-inter font-bold text-white text-xl">{userStats.activeUsers}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-white/50 font-inter text-xs">Total Files</p>
                  <p className="font-inter font-bold text-white text-xl">{userStats.totalFiles}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-white/50 font-inter text-xs">Total Storage</p>
                  <p className="font-inter font-bold text-white text-xl">{formatBytes(userStats.totalStorage)}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 border-white/10 md:col-span-2">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <Clock className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-white/50 font-inter text-xs">New Signups (Last 7 Days)</p>
                  <p className="font-inter font-bold text-white text-2xl">{userStats.recentSignups}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Content Management Tab */}
        {activeTab === 'content' && (
          <div className="space-y-4">
            {/* Create/Edit Form */}
            <Card className="p-4 border-white/10">
              <h3 className="font-inter font-bold text-white mb-4">
                {editingContent ? 'Edit Content' : 'Create New Content'}
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-white/70 font-inter text-xs mb-1">Type</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setNewContentType('change')}
                      className={`px-3 py-1.5 rounded text-xs font-inter font-medium transition-all ${
                        newContentType === 'change'
                          ? 'bg-primary text-black'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      Change
                    </button>
                    <button
                      onClick={() => setNewContentType('news')}
                      className={`px-3 py-1.5 rounded text-xs font-inter font-medium transition-all ${
                        newContentType === 'news'
                          ? 'bg-primary text-black'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      News
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-white/70 font-inter text-xs mb-1">Title</label>
                  <input
                    type="text"
                    value={newContentTitle}
                    onChange={(e) => setNewContentTitle(e.target.value)}
                    placeholder="Enter title..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm font-inter focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-white/70 font-inter text-xs mb-1">Description</label>
                  <textarea
                    value={newContentDesc}
                    onChange={(e) => setNewContentDesc(e.target.value)}
                    placeholder="Enter description..."
                    rows={3}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm font-inter focus:outline-none focus:border-primary resize-none"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveContent} variant="primary" size="sm">
                    {editingContent ? 'Update' : 'Create'}
                  </Button>
                  {editingContent && (
                    <Button
                      onClick={() => {
                        setEditingContent(null);
                        setNewContentTitle('');
                        setNewContentDesc('');
                      }}
                      variant="secondary"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </Card>

            {/* Content List */}
            <Card className="p-4 border-white/10">
              <h3 className="font-inter font-bold text-white mb-4">All Content</h3>
              <div className="space-y-2">
                {dashboardContent.length === 0 ? (
                  <p className="text-white/50 font-inter text-sm text-center py-8">
                    No content yet. Create your first item above.
                  </p>
                ) : (
                  dashboardContent.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-inter font-medium ${
                            item.type === 'change' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                          }`}>
                            {item.type}
                          </span>
                          <span className={`text-xs font-inter ${
                            item.visible ? 'text-green-400' : 'text-white/40'
                          }`}>
                            {item.visible ? 'Visible' : 'Hidden'}
                          </span>
                        </div>
                        <p className="font-inter font-medium text-white text-sm mb-1">{item.title}</p>
                        <p className="text-white/60 font-inter text-xs">{item.description}</p>
                        <p className="text-white/40 font-inter text-xs mt-1">
                          {new Date(item.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => toggleVisibility(item.id, item.visible)}
                          className={`px-2 py-1 rounded text-xs font-inter font-medium transition-all ${
                            item.visible
                              ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                              : 'bg-white/10 text-white/60 hover:bg-white/20'
                          }`}
                        >
                          {item.visible ? 'Hide' : 'Show'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingContent(item);
                            setNewContentTitle(item.title);
                            setNewContentDesc(item.description);
                            setNewContentType(item.type);
                          }}
                          className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-inter font-medium hover:bg-primary/30 transition-all"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteContent(item.id)}
                          className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-inter font-medium hover:bg-red-500/30 transition-all"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        )}

        {/* User Monitoring Tab */}
        {activeTab === 'users' && userStats && (
          <div className="space-y-4">
            <Card className="p-4 border-white/10">
              <h3 className="font-inter font-bold text-white mb-4">User Statistics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-white/50 font-inter text-xs mb-1">Total Users</p>
                  <p className="font-inter font-bold text-white text-xl">{userStats.totalUsers}</p>
                </div>
                <div>
                  <p className="text-white/50 font-inter text-xs mb-1">Active</p>
                  <p className="font-inter font-bold text-primary text-xl">{userStats.activeUsers}</p>
                </div>
                <div>
                  <p className="text-white/50 font-inter text-xs mb-1">Total Files</p>
                  <p className="font-inter font-bold text-white text-xl">{userStats.totalFiles}</p>
                </div>
                <div>
                  <p className="text-white/50 font-inter text-xs mb-1">Storage Used</p>
                  <p className="font-inter font-bold text-white text-xl">{formatBytes(userStats.totalStorage)}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-inter font-bold text-white">Recent Activity</h3>
                <Button onClick={loadData} variant="secondary" size="sm">
                  Refresh
                </Button>
              </div>
              <p className="text-white/50 font-inter text-sm text-center py-8">
                User activity monitoring will be available soon.
              </p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

